import { usePageVisibilityContext } from "./PageVisibilityContext";

// ============================================
// Types
// ============================================

export interface UsePageVisibilityResult {
  /** Whether the page/tab is currently visible (Page Visibility API) */
  isVisible: boolean;
  /** Whether the browser window currently has focus */
  isFocused: boolean;
  /** 
   * Whether the app is actively being used.
   * True only when the tab is visible AND the window has focus.
   * Use this for most use cases like pausing polling.
   */
  isActive: boolean;
  /** The raw visibilityState from the document */
  visibilityState: DocumentVisibilityState;
}

// ============================================
// Hook
// ============================================

/**
 * Hook to access the current page visibility and focus state.
 *
 * @example
 * ```tsx
 * const { isActive, isVisible, isFocused } = usePageVisibility();
 *
 * // isActive is true only when tab is visible AND window has focus
 * // Use isActive for most cases (polling, animations, etc.)
 * useEffect(() => {
 *   if (isActive) {
 *     console.log('User is actively using the app');
 *   }
 * }, [isActive]);
 * ```
 */
export const usePageVisibility = (): UsePageVisibilityResult => {
  const { isVisible, isFocused, isActive, visibilityState } = usePageVisibilityContext();

  return {
    isVisible,
    isFocused,
    isActive,
    visibilityState,
  };
};